import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/Button';
import { useDispatch, useSelector } from 'react-redux';
import useAxiosPrivate from '../hooks/useAxiosPrivate';
import { toast } from 'react-toastify';
import { setUserData } from '../store/userSlice';

function Step2({ setStep }) {
  const axiosPrivate = useAxiosPrivate();
  const dispatch = useDispatch();
  const { userId } = useSelector((state) => state.auth);
  const registrationData = useSelector((state) => state.user.registrationData);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [disabled, setDisabled] = useState(true);
  const [ghostPhoto, setGhostPhoto] = useState(null);
  const [ghostStyle, setGhostStyle] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [photos, setPhotos] = useState({});
  const fileInputs = useRef({});
  const justDragged = useRef(false);

  const checkPhotos = () => {
    const hasMainPhoto = Object.keys(photos).length > 0;
    console.log('hasMainPhoto', hasMainPhoto);

    setDisabled(!hasMainPhoto);
  };

  // Убрали очистку фотографий при входе на шаг
  // чтобы они сохранялись при обновлении страницы или переходах

  useEffect(() => {
    checkPhotos();
  }, [photos]);

  useEffect(() => {
    if (!Array.isArray(registrationData.photos)) return;

    const nextPhotos = registrationData.photos.reduce((acc, photo, index) => {
      if (!photo) return acc;

      acc[index + 1] = typeof photo === 'string' ? { url: photo, isNew: false } : photo;

      return acc;
    }, {});

    if (Object.keys(nextPhotos).length > 0) {
      setPhotos(nextPhotos);
    }
  }, [registrationData.photos]);

  const handlePhotoSelect = (photoId) => {
    fileInputs.current[photoId]?.click();
  };

  const handleFileChange = (e, photoId) => {
    const file = e.target.files[0];
    if (file) {
      setPhotos((prev) => ({
        ...prev,
        [photoId]: {
          file: file,
          preview: URL.createObjectURL(file),
          isNew: true
        }
      }));
    }
  };

  const reorderPhotos = (fromPhotoId, toPhotoId) => {
    console.log(`Reordering photos: from ${fromPhotoId} to ${toPhotoId}`);
    setPhotos((prev) => {
      const newPhotos = { ...prev };

      // Получаем фото, которое перетаскиваем
      const draggedPhoto = newPhotos[fromPhotoId];
      const targetPhoto = newPhotos[toPhotoId];

      if (draggedPhoto && targetPhoto) {
        // Меняем местами
        console.log('Swapping photos:', draggedPhoto, 'with', targetPhoto);
        newPhotos[fromPhotoId] = targetPhoto;
        newPhotos[toPhotoId] = draggedPhoto;
      } else if (draggedPhoto && !targetPhoto) {
        // Перемещаем в пустое место
        console.log('Moving photo to empty slot:', draggedPhoto);
        newPhotos[toPhotoId] = draggedPhoto;
        delete newPhotos[fromPhotoId];
      }

      console.log('New photos state:', newPhotos);
      return newPhotos;
    });
  };

  const extractKeyFromUrl = (url) => {
    if (!url) return null;
    try {
      // Для presigned URLs и обычных S3 URLs
      if (url.includes('amazonaws.com/')) {
        const parts = url.split('amazonaws.com/');
        if (parts.length > 1) {
          // Убираем query параметры
          return decodeURIComponent(parts[1].split('?')[0]);
        }
      }
      // Fallback - пытаемся извлечь последнюю часть пути
      const pathname = new URL(url).pathname;
      return pathname.startsWith('/') ? pathname.substring(1) : pathname;
    } catch (e) {
      console.warn('Failed to extract key from URL:', url, e);
      return null;
    }
  };

  const handleNext = async () => {
    setIsLoading(true);
    try {
      // Сначала загружаем все новые фотографии
      const newPhotos = Object.entries(photos).filter(([photoId, photo]) => photo.isNew && photo.file);

      // Загружаем новые файлы и получаем их ключи
      const uploadedPhotoKeys = {};
      for (const [photoId, photoData] of newPhotos) {
        try {
          const formData = new FormData();
          formData.append('file', photoData.file);

          console.log('Uploading new photo...');
          const response = await axiosPrivate.post('/users/photo', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });

          // Сохраняем ключ загруженной фотографии
          uploadedPhotoKeys[photoId] = response.data.data.key;
        } catch (error) {
          console.error('Failed to upload photo:', error);
          throw error;
        }
      }

      // Теперь создаем правильный порядок фотографий
      const orderedPhotoKeys = [];
      for (let i = 1; i <= 4; i++) {
        const photo = photos[i];
        if (photo) {
          if (photo.isNew) {
            // Новая фотография - используем загруженный ключ
            orderedPhotoKeys.push(uploadedPhotoKeys[i]);
          } else {
            // Существующая фотография - извлекаем ключ из URL
            const key = extractKeyFromUrl(photo.url);
            if (key) {
              orderedPhotoKeys.push(key);
            }
          }
        }
      }

      // Отправляем обновленный порядок фотографий на сервер
      if (orderedPhotoKeys.length > 0) {
        try {
          console.log('Sending new photos order to server:', orderedPhotoKeys);
          const orderResponse = await axiosPrivate.put('/users/photos-order', {
            photos: orderedPhotoKeys
          });
          console.log('Photos order updated successfully:', orderResponse.data);
        } catch (error) {
          // Если эндпоинт не существует, пропускаем ошибку
          console.warn('Failed to update photos order, endpoint may not exist:', error);
        }
      }

      // Перезагружаем данные пользователя, чтобы обновить порядок фотографий в Redux
      try {
        const updatedUserResponse = await axiosPrivate.get(`/users/${userId}`);
        dispatch(setUserData(updatedUserResponse.data));
      } catch (updateError) {
        console.warn('Failed to update user data in Redux:', updateError);
      }

      toast.success('Фотографии загружены!');

      // Переходим к следующему шагу
      setStep(3);
    } catch (error) {
      console.error('Ошибка загрузки фотографий:', error);
      toast.error('Ошибка при загрузке фотографий');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    justDragged.current = true;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    document.body.classList.add('dragging');
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    document.body.classList.remove('dragging');
    setTimeout(() => {
      justDragged.current = false;
    }, 0);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      e.currentTarget.classList.add('drag-over');
    }
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('drag-over');
  };

  const handleDrop = (e, targetPhotoId) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');

    console.log('handleDrop', targetPhotoId);

    if (draggedIndex !== null && draggedIndex !== targetPhotoId) {
      reorderPhotos(draggedIndex, targetPhotoId);
    }
  };

  const handleDeletePhoto = (photoId) => {
    setPhotos((prev) => {
      const newPhotos = { ...prev };
      delete newPhotos[photoId];
      return newPhotos;
    });
  };

  // 💡 Touch Drag Support Below

  const handleTouchStart = (e, photoId) => {
    const photo = photos[photoId];
    if (!photo || (!photo.url && !photo.preview)) return;

    setDraggedIndex(photoId);
    setGhostPhoto(photo.url || photo.preview);

    e.preventDefault();
  };

  const handleTouchMove = (e) => {
    if (draggedIndex === null) return;

    const touch = e.touches[0];
    setGhostStyle({
      top: touch.clientY - 80 + 'px',
      left: touch.clientX - 80 + 'px',
      position: 'fixed',
      width: '160px',
      height: '160px',
      zIndex: 1000,
      pointerEvents: 'none',
      opacity: 0.8
    });

    e.preventDefault();
  };

  const handleTouchEnd = (e) => {
    if (draggedIndex === null) return;

    const touch = e.changedTouches[0];
    const dropTarget = document.elementFromPoint(touch.clientX, touch.clientY);
    const dropIndex = Number(dropTarget?.closest('[data-index]')?.getAttribute('data-index'));

    if (!isNaN(dropIndex) && dropIndex !== draggedIndex) {
      reorderPhotos(draggedIndex, dropIndex);
    }

    setDraggedIndex(null);
    setGhostPhoto(null);
  };

  return (
    <div className="flex flex-col items-center w-full h-full overflow-y-auto overflow-x-hidden">
      <h1 className="font-raleway font-semibold mt-6 text-white text-[20px]">Добавьте фото</h1>
      <h1 className="font-raleway font-light mt-2 text-white text-[16px]">Минимум одно, а лучше – все четыре</h1>

      <div className="w-[343px] h-[343px] mt-5 grid grid-cols-2 gap-[15px]">
        {[1, 2, 3, 4].map((photoId) => (
          <React.Fragment key={photoId}>
            <input
              type="file"
              ref={(el) => (fileInputs.current[photoId] = el)}
              onChange={(e) => handleFileChange(e, photoId)}
              accept="image/*"
              className="hidden"
            />
            <div
              key={photoId}
              data-index={photoId}
              draggable={!!photos[photoId]}
              onClick={() => {
                if (justDragged.current) return;
                handlePhotoSelect(photoId);
              }}
              onDragStart={(e) => handleDragStart(e, photoId)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, photoId)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, photoId)}
              onTouchStart={(e) => handleTouchStart(e, photoId)}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              className={`w-[164px] h-[164px] border-[1px] rounded-[16px] border-[#233636] bg-[#022424] relative flex items-center justify-center cursor-pointer
              ${draggedIndex === photoId ? 'opacity-50' : ''}
              transition-all duration-200 ease-in-out
              hover:border-[#A1F69E]
              drag-over:border-[#A1F69E]
              drag-over:scale-105`}
            >
              {photoId === 1 && (
                <div className="absolute top-0 left-0 z-10">
                  <img alt="Главное фото" src="/icons/main_photo_label.svg" />
                </div>
              )}
              {photos[photoId]?.preview ? (
                <>
                  <img
                    src={photos[photoId].preview}
                    alt={`Photo ${photoId}`}
                    className="w-full h-full object-cover rounded-[16px] select-none"
                  />
                  {photoId === 1 && (
                    <div className="absolute bottom-0 left-0 right-0 h-[40px] flex items-center justify-center bg-gradient-to-t from-[#032B2B] to-transparent rounded-b-[16px]">
                      <span className="text-white font-raleway text-[14px]">Главное фото</span>
                    </div>
                  )}
                  <div
                    className="absolute top-[8px] right-[8px] pointer-events-auto z-10 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePhoto(photoId);
                    }}
                  >
                    <img src="/icons/delete_img.svg" className="w-[16px] h-[16px]" alt="Удалить" />
                  </div>
                </>
              ) : photos[photoId]?.url ? (
                <>
                  <img
                    src={photos[photoId].url}
                    alt={`Photo ${photoId}`}
                    className="w-full h-full object-cover rounded-[16px] select-none"
                  />
                  {photoId === 1 && (
                    <div className="absolute bottom-0 left-0 right-0 h-[40px] flex items-center justify-center bg-gradient-to-t from-[#032B2B] to-transparent rounded-b-[16px]">
                      <span className="text-white font-raleway text-[14px]">Главное фото</span>
                    </div>
                  )}
                  <div
                    className="absolute top-[8px] right-[8px] pointer-events-auto z-10 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePhoto(photoId);
                    }}
                  >
                    <img src="/icons/delete_img.svg" className="w-[16px] h-[16px]" alt="Удалить" />
                  </div>
                </>
              ) : (
                <>
                  <img alt="Камера" src="/icons/camera.svg" />
                  {photoId !== 1 && (
                    <span className="absolute top-1 right-1 text-white font-raleway text-[15px] font-medium">
                      +25 <img alt="myta-coin" src="/icons/myta-coin.svg" className="inline w-[16px]" />
                    </span>
                  )}
                </>
              )}
            </div>
          </React.Fragment>
        ))}
      </div>

      <h1 className="font-raleway font-light mt-[15px] text-white text-[16px]">Перетащите, чтобы изменить порядок</h1>

      <div className="fixed bottom-[20px]">
        <Button onClick={handleNext} disabled={disabled || isLoading}>
          {isLoading ? 'Сохранение...' : 'Далее'}
        </Button>
      </div>

      {/* 👻 Призрак фото для тача */}
      {ghostPhoto && (
        <img
          src={ghostPhoto}
          style={ghostStyle}
          className="rounded-[16px] shadow-lg transition-none"
          alt="Призрак фото"
        />
      )}
    </div>
  );
}

export default Step2;
